import { useState } from "react";
import { Envelope, ArrowRight, CheckCircle, X } from "@phosphor-icons/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { acknowledgeCycleInbox } from "../lib/api";

/**
 * MPCycleInboxBanner — surfaces Owner submissions that MP hasn't acknowledged
 * yet. Renders at the top of the dashboard overview (right pane when nothing
 * is selected). Owner-direct binding per ADR-MP-005 / cycle-5c+25-fix21:
 *
 *   - GET /api/mp/cycle-inbox aggregates "new since marker" across 3 streams
 *     (Owner decisions / workbook pick-acks / view-audit touches)
 *   - POST /api/mp/cycle-acknowledge bumps the marker to NOW (auth-required)
 *
 * The discipline binding: Owner can SEE whether MP has read recent
 * submissions. After MP confirms in chat that it has read everything,
 * the Owner clicks "Mark MP caught up" to clear the banner.
 *
 * Hidden when total_new === 0 (MP is caught up).
 */
export default function MPCycleInboxBanner({ inbox, onAcknowledged }) {
  const { isAuthenticated, getIdTokenClaims } = useAuth0();
  const navigate = useNavigate();
  const [acking, setAcking] = useState(false);
  const [error, setError] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  if (!inbox || inbox.total_new === 0 || dismissed) return null;

  const decisions = inbox.new_decisions || [];
  const pickAcks = inbox.new_pick_acks || [];
  const viewTouches = inbox.new_view_touches || [];

  const handleAcknowledge = async () => {
    if (!isAuthenticated) {
      setError("Sign in first to acknowledge.");
      return;
    }
    setAcking(true);
    setError(null);
    try {
      const claims = await getIdTokenClaims();
      const token = claims?.__raw;
      if (!token) throw new Error("Could not retrieve ID token.");
      await acknowledgeCycleInbox(token);
      if (typeof onAcknowledged === "function") {
        await onAcknowledged();
      }
      setDismissed(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Acknowledge failed";
      setError(detail);
    } finally {
      setAcking(false);
    }
  };

  const formatMarker = (iso) => {
    // Display-layer timezone canon per ADR-MP-014 (cycle-5c+25-fix52).
    // Compact format with short TZ label: "Jun 17 14:32 AEST".
    if (!iso) return "—";
    try {
      const t = new Date(iso);
      if (Number.isNaN(t.getTime())) return iso;
      return new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZoneName: "short",
      }).format(t);
    } catch {
      return iso;
    }
  };

  const navigateToItem = (itemType, itemId) => {
    navigate(`/?item=${itemType}:${itemId}`);
  };

  return (
    <div
      className="surface-card mb-4 overflow-hidden"
      style={{
        borderColor: "hsl(var(--status-warning) / 0.45)",
        borderWidth: "1px",
        background: "hsl(var(--status-warning) / 0.06)",
      }}
      data-testid="mp-cycle-inbox-banner"
    >
      {/* Header */}
      <div
        className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 border-b"
        style={{ borderColor: "hsl(var(--status-warning) / 0.30)" }}
      >
        <Envelope
          size={22}
          weight="duotone"
          style={{ color: "hsl(var(--status-warning))", flexShrink: 0, marginTop: "2px" }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3
              className="text-sm font-semibold"
              style={{ color: "hsl(var(--foreground))" }}
              data-testid="mp-cycle-inbox-banner-title"
            >
              MP hasn&apos;t acknowledged{" "}
              <span
                className="text-mono"
                style={{
                  fontSize: "0.85rem",
                  background: "hsl(var(--status-warning) / 0.20)",
                  borderRadius: "0.25rem",
                  padding: "0 0.4rem",
                  color: "hsl(var(--status-warning))",
                  fontWeight: 700,
                }}
                data-testid="mp-cycle-inbox-banner-count"
              >
                {inbox.total_new}
              </span>{" "}
              of your recent submissions yet
            </h3>
          </div>
          <p
            className="text-xs mt-1"
            style={{ color: "hsl(var(--muted-foreground))" }}
          >
            Since{" "}
            <span className="text-mono" style={{ color: "hsl(var(--foreground))" }}>
              {formatMarker(inbox.marker_last_acknowledged_at)}
            </span>
            {inbox.marker_default_used && (
              <span style={{ color: "hsl(var(--muted-foreground))" }}>
                {" "}(default 14-day window — MP has never been acknowledged on this surface)
              </span>
            )}
            . Click <strong>Mark MP caught up</strong> after MP confirms in chat that it has read them.
          </p>
          {error && (
            <p
              className="text-xs mt-2"
              style={{ color: "hsl(var(--status-critical))" }}
              data-testid="mp-cycle-inbox-banner-error"
            >
              {error}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Dismiss banner (does not bump marker)"
          title="Dismiss for this session (does not bump marker — banner returns on next load)"
          data-testid="mp-cycle-inbox-banner-dismiss"
        >
          <X size={14} />
        </button>
      </div>

      {/* Decisions list */}
      {decisions.length > 0 && (
        <SectionBlock
          icon="📝"
          label={`${decisions.length} owner decision${decisions.length === 1 ? "" : "s"}`}
        >
          {decisions.slice(0, 8).map((d, i) => (
            <button
              key={`d-${d.file_path}-${i}`}
              type="button"
              onClick={() => navigateToItem(d.item_type, d.item_id)}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-[hsl(var(--muted)/0.40)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              data-testid={`mp-cycle-inbox-decision-${d.ref || d.item_id}`}
            >
              {d.ref && (
                <span
                  className="text-mono shrink-0"
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    color: "hsl(var(--primary))",
                    background: "hsl(var(--primary) / 0.12)",
                    borderRadius: "0.25rem",
                    padding: "0.05rem 0.35rem",
                  }}
                >
                  [{d.ref}]
                </span>
              )}
              <span
                className="text-xs flex-1 truncate min-w-0"
                style={{ color: "hsl(var(--foreground))" }}
              >
                {d.title || d.item_id}
              </span>
              <span
                className="text-mono shrink-0 hidden sm:inline"
                style={{ fontSize: "0.6rem", color: "hsl(var(--muted-foreground))" }}
              >
                {d.submitted_at.slice(11, 16)} · {d.submitted_at.slice(0, 10)}
              </span>
              <span
                className="text-mono shrink-0 sm:hidden"
                style={{ fontSize: "0.6rem", color: "hsl(var(--muted-foreground))" }}
              >
                {d.submitted_at.slice(5, 10)}
              </span>
              <ArrowRight
                size={10}
                style={{ color: "hsl(var(--muted-foreground))", flexShrink: 0 }}
              />
            </button>
          ))}
          {decisions.length > 8 && (
            <div
              className="px-3 py-1 text-mono"
              style={{ fontSize: "0.6rem", color: "hsl(var(--muted-foreground))" }}
            >
              + {decisions.length - 8} more (open the card to see latest)
            </div>
          )}
        </SectionBlock>
      )}

      {/* Pick-acks list */}
      {pickAcks.length > 0 && (
        <SectionBlock
          icon="📓"
          label={`${pickAcks.length} workbook pick-ack${pickAcks.length === 1 ? "" : "s"}`}
        >
          {pickAcks.slice(0, 5).map((a, i) => (
            <button
              key={`a-${a.file_path}-${i}`}
              type="button"
              onClick={() => navigateToItem("workbook", a.workbook_id)}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-[hsl(var(--muted)/0.40)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              data-testid={`mp-cycle-inbox-pickack-${a.workbook_id}`}
            >
              <span
                className="text-xs flex-1 truncate min-w-0"
                style={{ color: "hsl(var(--foreground))" }}
              >
                {a.workbook_id}
                {a.accept_all && (
                  <span
                    className="ml-2 text-mono"
                    style={{ fontSize: "0.55rem", color: "hsl(var(--status-success))" }}
                  >
                    [accept-all]
                  </span>
                )}
              </span>
              <span
                className="text-mono shrink-0 hidden sm:inline"
                style={{ fontSize: "0.6rem", color: "hsl(var(--muted-foreground))" }}
              >
                {a.submitted_at.slice(11, 16)} · {a.submitted_at.slice(0, 10)}
              </span>
              <span
                className="text-mono shrink-0 sm:hidden"
                style={{ fontSize: "0.6rem", color: "hsl(var(--muted-foreground))" }}
              >
                {a.submitted_at.slice(5, 10)}
              </span>
              <ArrowRight
                size={10}
                style={{ color: "hsl(var(--muted-foreground))", flexShrink: 0 }}
              />
            </button>
          ))}
        </SectionBlock>
      )}

      {/* View-touches list (compact) */}
      {viewTouches.length > 0 && (
        <SectionBlock
          icon="👁"
          label={`${viewTouches.length} "I worked on this" touch${viewTouches.length === 1 ? "" : "es"}`}
        >
          {viewTouches.slice(0, 5).map((t, i) => (
            <button
              key={`t-${t.item_id}-${i}`}
              type="button"
              onClick={() => navigateToItem(t.item_type, t.item_id)}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-left hover:bg-[hsl(var(--muted)/0.40)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              data-testid={`mp-cycle-inbox-touch-${t.item_id}`}
            >
              <span
                className="text-xs flex-1 truncate min-w-0"
                style={{ color: "hsl(var(--foreground))" }}
              >
                {t.item_type}:{t.item_id}
              </span>
              <span
                className="text-mono shrink-0 hidden sm:inline"
                style={{ fontSize: "0.6rem", color: "hsl(var(--muted-foreground))" }}
              >
                {t.touched_at.slice(11, 16)} · {t.touched_at.slice(0, 10)}
              </span>
              <span
                className="text-mono shrink-0 sm:hidden"
                style={{ fontSize: "0.6rem", color: "hsl(var(--muted-foreground))" }}
              >
                {t.touched_at.slice(5, 10)}
              </span>
              <ArrowRight
                size={10}
                style={{ color: "hsl(var(--muted-foreground))", flexShrink: 0 }}
              />
            </button>
          ))}
        </SectionBlock>
      )}

      {/* Action footer */}
      <div
        className="flex items-center gap-3 p-3 border-t"
        style={{
          borderColor: "hsl(var(--status-warning) / 0.30)",
          background: "hsl(var(--status-warning) / 0.04)",
        }}
      >
        <div className="flex-1" />
        <button
          type="button"
          onClick={handleAcknowledge}
          disabled={acking || !isAuthenticated}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-mono transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed hover:border-[hsl(var(--primary))]"
          style={{
            fontSize: "0.7rem",
            fontWeight: 700,
            background: "hsl(var(--background))",
            borderColor: "hsl(var(--primary))",
            color: "hsl(var(--primary))",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
          data-testid="mp-cycle-inbox-acknowledge-button"
          title={
            isAuthenticated
              ? "Bump the MP-cycle marker to NOW after MP confirms in chat it has read these"
              : "Sign in to acknowledge"
          }
        >
          <CheckCircle size={12} weight="bold" />
          {acking ? "Marking…" : "Mark MP caught up"}
        </button>
      </div>
    </div>
  );
}

function SectionBlock({ icon, label, children }) {
  return (
    <div className="py-2">
      <div
        className="px-4 pb-1 text-mono flex items-center gap-1.5"
        style={{
          fontSize: "0.6rem",
          fontWeight: 700,
          color: "hsl(var(--muted-foreground))",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        <span aria-hidden="true">{icon}</span> {label}
      </div>
      <div className="px-1">{children}</div>
    </div>
  );
}
